import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Navbar } from "@/components/Navbar";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import {
  User as UserIcon, Award, BookOpen, Bot, MessageSquare, Check,
  Trophy, Calendar, Mail, Edit3, LogOut, ArrowRight, ShieldCheck
} from "lucide-react";

export default function Profile() {
  const { user, logout, refresh } = useAuth();
  const [stats, setStats] = useState(null);
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(user?.full_name || "");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/profile/stats");
        setStats(data);
      } catch {
        toast.error("Couldn't load profile stats");
      }
    })();
  }, []);

  useEffect(() => { if (user?.full_name) setName(user.full_name); }, [user]);

  const saveName = async () => {
    if (!name.trim() || name.trim() === user.full_name) { setEditing(false); return; }
    setSaving(true);
    try {
      await api.patch("/auth/me", { full_name: name.trim() });
      await refresh();
      toast.success("Profile updated");
      setEditing(false);
    } catch {
      toast.error("Couldn't save");
    } finally {
      setSaving(false);
    }
  };

  const joined = stats?.joined ? new Date(stats.joined).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" }) : "";
  const initials = user?.full_name?.split(" ").map(n => n[0]).slice(0, 2).join("").toUpperCase() || "?";

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <main className="max-w-5xl mx-auto px-6 sm:px-8 lg:px-12 py-12" data-testid="profile-page">
        <div className="overline mb-3"><span className="red-bar" />Profile</div>
        <h1 className="font-heading text-4xl sm:text-5xl font-bold tracking-tight mb-10">Your account</h1>

        <div className="grid md:grid-cols-3 gap-6 mb-10">
          {/* Identity card */}
          <div className="card-tech p-8 md:col-span-2" data-testid="profile-identity-card">
            <div className="flex items-start gap-5">
              <div className="w-20 h-20 bg-zinc-950 text-white grid place-items-center font-heading font-bold text-2xl flex-shrink-0">
                {initials}
              </div>
              <div className="flex-1">
                {editing ? (
                  <div className="space-y-3">
                    <Label className="overline">Full name</Label>
                    <Input value={name} onChange={(e) => setName(e.target.value)} className="rounded-sm max-w-sm" data-testid="profile-name-input" />
                    <div className="flex gap-2">
                      <button onClick={saveName} disabled={saving} className="btn-primary text-sm" data-testid="profile-save-btn">
                        <Check className="w-4 h-4" /> {saving ? "Saving…" : "Save"}
                      </button>
                      <button onClick={() => { setEditing(false); setName(user.full_name); }} className="btn-outline text-sm" data-testid="profile-cancel-btn">
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="font-heading text-3xl font-bold tracking-tight" data-testid="profile-name">{user?.full_name}</div>
                    <div className="flex items-center gap-2 text-sm text-zinc-600 mt-2">
                      <Mail className="w-3.5 h-3.5" /> <span>{user?.email}</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-zinc-600 mt-1">
                      <Calendar className="w-3.5 h-3.5" /> <span>Joined {joined}</span>
                    </div>
                    <button onClick={() => setEditing(true)} className="mt-5 text-sm font-semibold text-red-600 inline-flex items-center gap-1 hover:text-red-700" data-testid="profile-edit-btn">
                      <Edit3 className="w-3.5 h-3.5" /> Edit name
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>

          <div className="card-tech p-6" data-testid="profile-cert-card">
            <div className="overline mb-2">Certificate</div>
            {stats?.certificate_earned ? (
              <>
                <Trophy className="w-7 h-7 text-red-600 mb-3" />
                <div className="font-heading text-xl font-bold">NICET L1 Certificate earned</div>
                <div className="flex items-center gap-3 mt-4">
                  <Link to="/certificate" className="inline-flex items-center gap-1 text-sm font-semibold text-red-600 hover:text-red-700" data-testid="profile-view-cert-link">
                    View <ArrowRight className="w-4 h-4" />
                  </Link>
                  <span className="text-zinc-300">·</span>
                  <Link to="/transcript" className="inline-flex items-center gap-1 text-sm font-semibold text-red-600 hover:text-red-700" data-testid="profile-view-transcript-link">
                    Transcript <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>
              </>
            ) : (
              <>
                <Award className="w-7 h-7 text-zinc-400 mb-3" />
                <div className="font-heading text-xl font-bold text-zinc-400">Not earned yet</div>
                <div className="flex items-center gap-3 mt-4">
                  <Link to="/final-exam" className="inline-flex items-center gap-1 text-sm font-semibold text-red-600 hover:text-red-700" data-testid="profile-go-exam-link">
                    Final exam <ArrowRight className="w-4 h-4" />
                  </Link>
                  <span className="text-zinc-300">·</span>
                  <Link to="/transcript" className="inline-flex items-center gap-1 text-sm font-semibold text-red-600 hover:text-red-700" data-testid="profile-view-transcript-link">
                    Transcript <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Stats */}
        <div className="mb-4"><div className="overline"><span className="red-bar" />Learning stats</div></div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
          {[
            { icon: BookOpen, label: "Lessons completed", value: stats?.lessons_completed ?? 0 },
            { icon: ShieldCheck, label: "Quizzes taken", value: stats?.quizzes_taken ?? 0 },
            { icon: Trophy, label: "Avg quiz score", value: `${stats?.avg_quiz_score ?? 0}%` },
            { icon: MessageSquare, label: "Tutor questions asked", value: stats?.tutor_questions_asked ?? 0 },
          ].map((s, i) => (
            <div key={s.label} className="card-tech p-6" data-testid={`profile-stat-${i}`}>
              <s.icon className="w-5 h-5 text-red-600 mb-3" />
              <div className="font-heading text-3xl font-bold">{s.value}</div>
              <div className="text-xs uppercase tracking-widest text-zinc-500 font-mono mt-1">{s.label}</div>
            </div>
          ))}
        </div>

        {/* Quick nav */}
        <div className="grid md:grid-cols-3 gap-4 mb-10">
          <Link to="/dashboard" className="card-tech p-6 group" data-testid="profile-nav-dashboard">
            <UserIcon className="w-5 h-5 text-red-600 mb-3" />
            <div className="font-heading text-lg font-bold group-hover:text-red-600 transition-colors">Dashboard</div>
            <div className="text-xs text-zinc-500 mt-1">Continue your curriculum</div>
          </Link>
          <Link to="/s98-review" className="card-tech p-6 group" data-testid="profile-nav-s98">
            <Bot className="w-5 h-5 text-red-600 mb-3" />
            <div className="font-heading text-lg font-bold group-hover:text-red-600 transition-colors">NYC S-98 Review</div>
            <div className="text-xs text-zinc-500 mt-1">FDNY + NYC Fire / Electrical Code</div>
          </Link>
          <Link to="/final-exam" className="card-tech p-6 group" data-testid="profile-nav-final">
            <Award className="w-5 h-5 text-red-600 mb-3" />
            <div className="font-heading text-lg font-bold group-hover:text-red-600 transition-colors">Final Practice Exam</div>
            <div className="text-xs text-zinc-500 mt-1">50 questions · 70% to pass</div>
          </Link>
        </div>

        <div className="border-t border-zinc-200 pt-8 flex items-center justify-between flex-wrap gap-3">
          <div className="text-sm text-zinc-500">Signed in as <span className="font-semibold text-zinc-800">{user?.email}</span></div>
          <button onClick={() => { logout(); window.location.href = "/"; }} className="btn-outline text-sm" data-testid="profile-logout-btn">
            <LogOut className="w-4 h-4" /> Log out
          </button>
        </div>
      </main>
    </div>
  );
}
